import { useQuery } from '@tanstack/vue-query';

export type DeveloperProjectStatus = 'launch' | 'available' | 'sold' | 'unknown';

export type DeveloperProjectRow = {
  /** Bot projects.id, or `d-<id>` for a project published by a developer representative. */
  id: number | string;
  geniemapId: number | null;
  slug: string | null;
  name: string;
  district: string | null;
  status: DeveloperProjectStatus;
  isNewLaunch: boolean;
  handover: string | null;
  startOfSales: string | null;
  progressPercent: number | null;
  serviceChargePerSqft: number | null;
  finishes: string | null;
  unitsCount: number;
  coverImageUrl: string | null;
  coverVideoUrl: string | null;
  /** Link target for rep-created projects (/properties/d-<id>). */
  href?: string;
  isDeveloperSubmitted?: boolean;
};

export type DeveloperProjectsPayload = {
  rows: DeveloperProjectRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
};

export type UseDeveloperProjectsQueryArgs = {
  slug: MaybeRef<string>;
  page?: MaybeRef<number>;
  pageSize?: MaybeRef<number>;
  status?: MaybeRef<DeveloperProjectStatus | 'all'>;
  search?: MaybeRef<string>;
  sort?: MaybeRef<'activity' | 'units' | 'handover' | 'name'>;
};

export const useDeveloperProjectsQuery = (args: UseDeveloperProjectsQueryArgs) => {
  const authStore = useAuthStore();

  const queryString = computed(() => {
    const params = new URLSearchParams();
    params.set('page', String(unref(args.page) ?? 1));
    params.set('pageSize', String(unref(args.pageSize) ?? 16));
    const status = unref(args.status) ?? 'all';
    if (status && status !== 'all') params.set('status', status);
    const search = unref(args.search);
    if (search && search.trim()) params.set('search', search.trim());
    const sort = unref(args.sort);
    if (sort) params.set('sort', sort);
    return params.toString();
  });

  // Public endpoint — auth token forwarded when present but not required.
  // Guests on /developers/<slug> see the projects grid just like agents do.
  return useQuery({
    queryKey: computed(() => ['developers', 'projects', unref(args.slug), queryString.value, !!authStore.token]),
    queryFn: () =>
      $fetch<{ success: boolean; data: DeveloperProjectsPayload }>(
        `/api/developers/by-slug/${encodeURIComponent(unref(args.slug))}/projects?${queryString.value}`,
        authStore.token ? { headers: { Authorization: `Bearer ${authStore.token}` } } : {},
      ),
    enabled: computed(() => !!unref(args.slug)),
    select: (res) => res?.data ?? null,
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
    placeholderData: (prev) => prev,
  });
};
